<template>
  <div class="section-card">
    <!-- 头部 -->
    <div class="section-header">
      <div class="header-left">
        <span class="section-title">服务配置</span>
        <span class="status-badge" :class="killPigEnabled ? 'on' : 'off'">
          {{ killPigEnabled ? '运行中' : '已关闭' }}
        </span>
      </div>
      <NSwitch :value="killPigEnabled" @update:value="emit('update:killPigEnabled', $event)" size="large" :disabled="!hasRooms" />
    </div>

    <!-- 运行状态 -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-dot" :class="killPigEnabled ? 'dot-on' : 'dot-off'"></span>
        <span class="stat-text">{{ killPigEnabled ? '服务运行中' : '服务已暂停' }}</span>
      </div>
      <div class="stat-item">
        <template v-if="hasRooms">
          <span class="stat-val">{{ selectRooms.length }}</span>
          <span class="stat-unit">/ {{ roomList.length }} 房间</span>
        </template>
        <span v-else class="stat-warn">无可用房间，相关功能已锁定</span>
      </div>
    </div>

    <!-- 拉取数据（控制上游 WebSocket 与视频链接拉取/重试，由遥控器保存开关） -->
    <div class="field-card auto-bet-card">
      <div class="field-header">
        <span class="field-icon">D</span>
        <span class="field-label">拉取数据</span>
        <NSwitch v-model:value="input.PullDataEnabled" :disabled="pullDataLocked" size="medium" style="margin-left:auto" />
      </div>
      <div class="field-hint">
        <template v-if="pullDataLocked">
          已锁定：需先关闭相关开关
        </template>
        <template v-else>
          {{ input.PullDataEnabled ? '已开启：接收上游数据' : '已关闭：已断开连接' }}
        </template>
      </div>
    </div>

    <!-- 保持链接（真实下注，由遥控器保存开关） -->
    <div class="field-card auto-bet-card">
      <div class="field-header">
        <span class="field-icon">B</span>
        <span class="field-label">保持链接</span>
        <NSwitch v-model:value="input.AutoBetEnabled" size="medium" style="margin-left:auto" />
      </div>
      <div class="balance-row">
        <span class="balance-label">账号余额</span>
        <span class="balance-val">{{ realBalance != null ? '¥ ' + realBalance.toFixed(2) : '—' }}</span>
      </div>
    </div>

    <!-- 最低启动金额 -->
    <div class="field-card">
      <div class="field-header">
        <span class="field-icon">$</span>
        <span class="field-label">参与门槛</span>
      </div>
      <NInputNumber v-model:value="input.KillStartAmount" :show-button="false" :min="0" size="large" class="field-input" :disabled="!hasRooms" />
      <div class="field-hint">
        {{ input.KillStartAmount === 0 ? '不限制，所有批次均适用' : '参与金额 ≥ ' + input.KillStartAmount + ' 元才生效' }}
      </div>
    </div>

    <!-- 高级设置 (Ctrl+Shift+D 统一显示/隐藏) -->
    <template v-if="debugMode">
    <NCollapse>
      <NCollapseItem title="高级设置" name="advanced">
        <!-- 批次保护：换主播/换靴/新批次，都是“这一批怎么保护”，放同一组 -->
        <div class="sub-title">批次保护</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">H</span>
              <span class="config-label">批次启动保护</span>
            </div>
            <NInputNumber v-model:value="input.HeaderReserveJuNumber" :show-button="false" :max="1000" :min="1" size="large" />
            <span class="config-desc">换主播后前 {{ input.HeaderReserveJuNumber }} 轮暂不干预</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">T</span>
              <span class="config-label">批次收尾保护</span>
            </div>
            <NInputNumber v-model:value="input.EndReserveJuNumber" :show-button="false" :max="1000" :min="2" size="large" />
            <span class="config-desc">至少保留 {{ input.EndReserveJuNumber }} 轮，保持数据连续</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">N</span>
              <span class="config-label">新批次保护</span>
              <NSwitch v-model:value="input.SkipKillInNewGame" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">新批次前三轮暂不干预，保持数据均衡</span>
          </div>
        </div>

        <!-- 锚点复用：开关 + 冷却 + 闸门 + 随机，是一整条复用策略 -->
        <div class="sub-title">锚点复用</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">R</span>
              <span class="config-label">允许复用已用锚点</span>
              <NSwitch v-model:value="input.AnchorReuseEnabled" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">
              {{ input.AnchorReuseEnabled
                ? '被用作跳转目标的对局，熬过冷却后可再次作为跳转目标'
                : '关闭：对局被用作跳转目标后彻底作废' }}
            </span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">C</span>
              <span class="config-label">使用冷却(秒)</span>
            </div>
            <NInputNumber v-model:value="input.AnchorReuseCooldownSeconds" :show-button="false" :min="0" :max="3600" size="large" />
            <span class="config-desc">
              {{ input.AnchorReuseEnabled
                ? '用过的对局需等 ' + (input.AnchorReuseCooldownSeconds || 0) + ' 秒才能再次使用'
                : '刚播到的对局 ' + (input.AnchorReuseCooldownSeconds || 0) + ' 秒内不再作为跳转目标' }}
            </span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">L</span>
              <span class="config-label">复用最低可复用局</span>
            </div>
            <NInputNumber v-model:value="input.AnchorReuseMinFreshCount" :show-button="false" :min="0" :max="1000" size="large" />
            <span class="config-desc">
              {{ !input.AnchorReuseMinFreshCount
                ? '不限制：可复用局很少时容易反复挑同几局'
                : '同一类型（如“庄”）可复用局少于 ' + input.AnchorReuseMinFreshCount + ' 局时禁止复用（默认 5）' }}
            </span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">S</span>
              <span class="config-label">随机选局</span>
              <NSwitch v-model:value="input.AnchorRandomPickEnabled" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">
              {{ input.AnchorRandomPickEnabled
                ? '在“庄家不亏”的候选里随机挑，避开最近挑过的局'
                : '按最大净收益固定挑（序列可能反复是同一批局）' }}
            </span>
          </div>
        </div>

        <!-- 余额推送 -->
        <div class="sub-title">余额推送</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">¥</span>
              <span class="config-label">调额后推送</span>
              <NSwitch v-model:value="input.PushBalanceOnAdjust" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">调账后即时推送余额；关闭则仅落库，需重新登录生效</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">N</span>
              <span class="config-label">新账号余额入库</span>
              <NSwitch v-model:value="input.SaveNewUserBalance" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">新账号入库写入上游余额；关闭则余额存 0</span>
          </div>
        </div>

        <!-- 客户端安全 -->
        <div class="sub-title">客户端安全</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">D</span>
              <span class="config-label">调试器拦截</span>
              <NSwitch v-model:value="input.CloseOnDebugger" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">检测到调试器立即关闭浏览器（客户端与服务端双重校验）；关闭则仅记录不拦截</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">V</span>
              <span class="config-label">版本号限制</span>
              <NSwitch v-model:value="input.RequireExtVersion" size="medium" style="margin-left:auto" />
            </div>
            <span class="config-desc">开启后，插件的长连接、身份校验、截图、测速、业务转发（/api/game/balance、bet-* 等）都会校验版本；未上报或低于最低版本的插件一律拒绝。遥控器自己的接口不受影响</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">#</span>
              <span class="config-label">最低版本号</span>
            </div>
            <NInput
              v-model:value="input.RequiredExtVersion"
              size="large"
              placeholder="如 2.0.1"
              :disabled="!input.RequireExtVersion"
            />
            <span class="config-desc">
              {{ !input.RequireExtVersion
                ? '开关关闭时不校验'
                : (input.RequiredExtVersion
                  ? '插件上报版本 ≥ ' + input.RequiredExtVersion + ' 才允许连接（按数字逐段比较）'
                  : '留空 = 只要求上报版本号，不比较大小') }}
            </span>
          </div>
        </div>

        <!-- 主播托管策略 -->
        <div class="sub-title">主播托管策略</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">D</span>
              <span class="config-label">重名避让</span>
              <NSwitch
                :value="input.NoHostingOnDuplicateDealer"
                size="medium"
                style="margin-left:auto"
                @update:value="onDupStrategyChange('avoid', $event)"
              />
            </div>
            <span class="config-desc">
              同一主播同时出现在多张牌台时，这些房间自动避让、不参与托管（默认关闭）。
              与「智能清理」二选一
            </span>
          </div>

          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">S</span>
              <span class="config-label">智能清理</span>
              <NSwitch
                :value="input.SmartAnchorClean"
                size="medium"
                style="margin-left:auto"
                @update:value="onDupStrategyChange('clean', $event)"
              />
            </div>
            <span class="config-desc">
              主播换桌时按 did/主播名清掉其他房间的同名残留锚点；开启后不再做重名避让
              （与「重名避让」二选一，默认开启）
            </span>
          </div>
        </div>

        <!-- 保持链接参数 -->
        <div class="sub-title">保持链接参数</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">I</span>
              <span class="config-label">间隔(分钟)</span>
            </div>
            <NInputNumber v-model:value="input.AutoBetIntervalMin" :show-button="false" :min="1" :max="120" size="large" />
            <span class="config-desc">每隔 {{ input.AutoBetIntervalMin }} 分钟保持一次链接</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">¥</span>
              <span class="config-label">单注金额</span>
            </div>
            <NInputNumber v-model:value="input.AutoBetAmount" :show-button="false" :min="0" size="large" />
            <span class="config-desc">每次保持链接下注 {{ input.AutoBetAmount }} 元</span>
          </div>
        </div>

        <!-- 数据管理 -->
        <div class="sub-title">数据管理</div>
        <div class="data-mgmt">
          <div class="data-mgmt-item">
            <div class="data-mgmt-info">
              <span class="data-mgmt-label">清空交易数据</span>
              <span class="data-mgmt-desc">清空后只统计此时间点之后的交易记录</span>
            </div>
            <NButton size="small" type="warning" @click="emit('clearBetCache')" ghost>清空交易</NButton>
          </div>
          <div class="data-mgmt-item">
            <div class="data-mgmt-info">
              <span class="data-mgmt-label">清空账号显示</span>
              <span class="data-mgmt-desc">清空后只显示此时间点后有交易记录的账号</span>
            </div>
            <NButton size="small" type="warning" @click="emit('clearAccountCache')" ghost>清空账号</NButton>
          </div>
        </div>
      </NCollapseItem>
    </NCollapse>
    </template>

  </div>
</template>

<script setup lang="ts">
import RoomVO from '@/ts/VO/RoomVO';
import type KillPigConfigVO from '@/ts/VO/KillPigConfigVO';
import { NCollapse, NCollapseItem, NInput, NInputNumber, NSwitch, NButton } from 'naive-ui';
import { inject, ref, computed, type Ref } from 'vue';

const props = defineProps<{
  input: KillPigConfigVO;
  killPigEnabled: boolean;
  realBalance: number;
  selectRooms: (string | number)[];
  roomList: RoomVO[];

}>();

const emit = defineEmits<{
  'update:killPigEnabled': [value: boolean];
  clearBetCache: [];
  clearAccountCache: [];
}>();

// 调试模式统一控制所有快捷显示
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

// 无可用房间时，依赖房间的开关（托管 / 保持链接 / 参与门槛）不应被打开
const hasRooms = computed(() => (props.roomList ?? []).length > 0);

/**
 * 重名策略二选一：
 *   avoid = 重名避让（打开避让 → 自动关掉智能清理）
 *   clean = 智能清理（打开清理 → 自动关掉重名避让）
 * 两个都关 = 重名照常（不做任何处理）。
 */
function onDupStrategyChange(mode: 'avoid' | 'clean', value: boolean) {
  if (mode === 'avoid') {
    props.input.NoHostingOnDuplicateDealer = value;
    if (value) props.input.SmartAnchorClean = false;
  } else {
    props.input.SmartAnchorClean = value;
    if (value) props.input.NoHostingOnDuplicateDealer = false;
  }
}

// 托管（用户当前勾选的房间，selectRooms 实时反映开关状态）或“保持链接”开启时，
// 拉取数据被锁定为开启，遥控器上不可关闭。
// 注意：锁定只看【当前 UI 意图】（selectRooms / AutoBetEnabled），不要依赖 roomList.OpenKillPig——
// 后者只有保存成功后才同步，会出现“关掉托管开关但拉取数据仍被锁、需刷新才解锁”的 BUG。
const pullDataLocked = computed(() =>
  !!props.input.AutoBetEnabled ||
  (props.selectRooms?.length ?? 0) > 0);
</script>

<style scoped lang="scss">
$gold: #d8b25f;
$gold-hi: #f6e3b6;
$gold-lo: #a87c39;
$muted: #9b8f79;
$faint: #6d6454;
$text: #efe6d4;
$text-2: #cbbda2;
$warn: #d9a24f;
$danger: #cd6a53;
$success: #7ec9a4;
$radius: 12px;

.section-card {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(216,178,95,.22); border-radius: 2px; }
}

// ---------- header ----------
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0,0,0,.6));
  }

  .status-badge {
    font-size: 10.5px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    letter-spacing: .8px;
    border: 1px solid transparent;

    &.on {
      color: #f8e9c4;
      background: linear-gradient(180deg, rgba(216,178,95,.26), rgba(168,124,57,.12));
      border-color: rgba(240,208,138,.48);
      box-shadow:
        0 0 16px -7px rgba(240,208,138,.55),
        inset 0 1px 0 rgba(255,242,208,.16);
    }
    &.off {
      color: $muted;
      background: rgba(255,240,210,.04);
      border-color: rgba(216,178,95,.14);
    }
  }
}

// ---------- tile row ----------
.tile-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-image: linear-gradient(160deg, rgba(255,240,210,.05), rgba(255,240,210,.015));
  border: 1px solid rgba(216,178,95,.14);
  border-radius: $radius;
  padding: 14px 16px;
}

.tile-label {
  font-size: 14px;
  font-weight: 600;
  color: $text-2;
}

.tile-hint {
  font-size: 11px;
  color: $muted;
  margin-top: -12px;
  padding-left: 2px;
}

// ---------- stats bar ----------
.stats-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $muted;
}

.stat-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;

  &.dot-on  {
    background: linear-gradient(180deg, #fdf0cd, $gold);
    box-shadow: 0 0 8px rgba(240,208,138,.60);
    animation: kp-dot-pulse 3.2s ease-in-out infinite;
  }
  &.dot-off { background: $faint; }
}

.stat-val {
  font-weight: 700;
  font-size: 16px;
  color: $gold-hi;
  font-family: var(--kp-mono);
}

.stat-unit {
  font-size: 11px;
  color: $muted;
}

.stat-warn {
  color: $warn;
  font-weight: 600;
}

// ---------- field card ----------
.field-card {
  background-image: linear-gradient(160deg, rgba(255,240,210,.05), rgba(255,240,210,.015));
  border: 1px solid rgba(216,178,95,.14);
  border-radius: $radius;
  box-shadow: inset 0 1px 0 rgba(255,235,198,.06);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(216,178,95,.26), rgba(168,124,57,.10));
  color: $gold-hi;
  box-shadow:
    inset 0 0 0 1px rgba(240,208,138,.30),
    0 0 14px -8px rgba(240,208,138,.75);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: $text-2;
  letter-spacing: .3px;
}

.field-input {
  width: 100%;
}

.field-hint {
  font-size: 11px;
  color: $muted;
  line-height: 1.5;

  &.warn {
    color: $warn;
    margin-top: 4px;
  }
}

// ---------- 保持链接（紧凑卡片） ----------
.auto-bet-card {
  padding: 12px 14px;
  gap: 8px;
}

.auto-bet-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.auto-bet-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  :deep(.n-input-number) {
    width: 100%;
  }
}

.auto-bet-label {
  font-size: 11px;
  color: $muted;
}

// ---------- 实时余额 ----------
.balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(180deg, rgba(216,178,95,.10), rgba(216,178,95,.04));
  border: 1px solid rgba(240,208,138,.22);
  border-radius: 9px;
  padding: 10px 14px;
}

.balance-label {
  font-size: 12px;
  font-weight: 600;
  color: $muted;
  letter-spacing: .5px;
}

.balance-val {
  font-size: 18px;
  font-weight: 800;
  color: $gold-hi;
  letter-spacing: .6px;
  font-family: var(--kp-mono);
  text-shadow: 0 0 16px rgba(216,178,95,.45);
}

// ---------- sub-title ----------
.sub-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  margin-bottom: 10px;
  margin-top: 18px;
  padding-left: 2px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    flex: none;
    transform: rotate(45deg);
    background: linear-gradient(135deg, #fdf3da, #c39a4f);
    box-shadow: 0 0 8px 0 rgba(240,208,138,.5);
  }

  &:first-child { margin-top: 8px; }
}

// ---------- config grid + cards ----------
.config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.config-card {
  background-image: linear-gradient(160deg, rgba(255,240,210,.05), rgba(255,240,210,.015));
  border: 1px solid rgba(216,178,95,.14);
  border-radius: $radius;
  box-shadow: inset 0 1px 0 rgba(255,235,198,.06);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.single {
    grid-column: 1 / -1;
  }

  .config-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .config-icon {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: linear-gradient(180deg, rgba(216,178,95,.26), rgba(168,124,57,.10));
    color: $gold-hi;
    box-shadow:
      inset 0 0 0 1px rgba(240,208,138,.30),
      0 0 12px -7px rgba(240,208,138,.75);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  .config-label {
    font-size: 12px;
    font-weight: 600;
    color: $text-2;
    letter-spacing: .3px;
  }

  .config-desc {
    font-size: 11px;
    color: $muted;
    line-height: 1.4;
    // 同一行里“数字卡”比“开关卡”多一个输入框，把说明贴底，视觉上对齐
    margin-top: auto;
  }
}

// ---------- data management ----------
.data-mgmt {
  background-image: linear-gradient(160deg, rgba(255,240,210,.05), rgba(255,240,210,.015));
  border: 1px solid rgba(216,178,95,.14);
  border-radius: $radius;
  box-shadow: inset 0 1px 0 rgba(255,235,198,.06);
  padding: 4px 0;
  overflow: hidden;
}

.data-mgmt-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  transition: background .15s;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(216,178,95,.10);
  }

  &:hover { background: rgba(216,178,95,.05); }
}

.data-mgmt-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  margin-right: 12px;
}

.data-mgmt-label {
  font-size: 13px;
  font-weight: 600;
  color: $text;
}

.data-mgmt-desc {
  font-size: 11px;
  color: $muted;
  line-height: 1.4;
}

// ---------- responsive ----------
@media (max-width: 480px) {
  .section-card { padding: 14px; gap: 14px; }
  .config-grid { grid-template-columns: 1fr; }
}
</style>
